import React, { useMemo } from 'react';
import { Dropdown, DropdownCalendar, Input, InputNumber } from '@cognitiv/cyprus-ui';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { selectBatchDefinitions } from 'ducks/batch-definitions/selectors';
import { useAppSelector } from 'ducks/hooks';
import { selectIntervalsList } from 'ducks/intervals-list/selectors';
import { selectScheduledBatchId } from 'ducks/scheduled-batch/selectors';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { useManageScheduleBatchContext } from 'modals/batches/context/ManageScheduleBatchContext';
import { shallowEqual } from 'react-redux';
import { formatDate } from 'utils/dates';
import { getValidationError } from 'utils/errors';
import { week_days } from 'utils/helpers';
import { checkNullableDate } from 'utils/modal/helpers';
import { getTimeZone } from 'utils/time-zone';

import cn from 'modals/batches/components/Components.module.scss';

export const ScheduledBatchFirstStep = () => {
  const { theme } = useSwitchTheme();

  const batch_definitions = useAppSelector(selectBatchDefinitions);
  const intervals = useAppSelector(selectIntervalsList);
  const batch_definition_id = useAppSelector(selectScheduledBatchId, shallowEqual);

  const { form, errors, onChange, onBatchDefinitionSelect, onIntervalSelect, onWeekDaySelect, onFinishDateSelect } =
    useManageScheduleBatchContext();

  const {
    scheduled_batch_id,
    scheduled_batch_name,
    interval_frequency,
    interval_name,
    next_datetime_formatted,
    finish_datetime_formatted,
    start_datetime_formatted,
    batch_definition_name,
    interval_bitmask,
  } = form;

  const is_nullable__date = useMemo(() => checkNullableDate(finish_datetime_formatted), [finish_datetime_formatted]);

  return (
    <div>
      <Input
        auto_focus
        label="Scheduled Batch Name"
        value={scheduled_batch_name}
        margin="0px 0px 0px 0px"
        onChange={(item: string) => onChange({ scheduled_batch_name: item })}
        error_message={errors.find((error) => error.key === 'scheduled_batch_name')?.error}
        error_level={getValidationError({ errors, key: 'scheduled_batch_name' }) ? ERROR_LEVEL.ERROR : undefined}
      />
      {!batch_definition_id && (
        <Dropdown
          label="Batch Definition"
          options={batch_definitions}
          scroll_height={250}
          value={batch_definition_name}
          option_key="batch_definition_name"
          margin="16px 0px 0px 0px"
          onSelect={onBatchDefinitionSelect}
          error_message={errors.find((error) => error.key === 'batch_definition_name')?.error}
          error_level={getValidationError({ errors, key: 'batch_definition_name' }) ? ERROR_LEVEL.ERROR : undefined}
        />
      )}
      <div className={cn.interval}>
        <InputNumber
          label="Repeat Every"
          value={interval_frequency}
          width="calc(50% - 7px)"
          margin="16px 7px 0px 0px"
          onChange={(item: { value: number }) => onChange({ interval_frequency: item.value })}
          error_message={errors.find((error) => error.key === 'interval_frequency')?.error}
          error_level={getValidationError({ errors, key: 'interval_frequency' }) ? ERROR_LEVEL.ERROR : undefined}
        />
        <Dropdown
          label="Interval"
          options={intervals}
          value={interval_name}
          option_key="interval_name"
          onSelect={onIntervalSelect}
          margin="16px 0px 0px 7px"
          width="calc(50% - 7px)"
          error_message={errors.find((error) => error.key === 'interval_name')?.error}
          error_level={getValidationError({ errors, key: 'interval_name' }) ? ERROR_LEVEL.ERROR : undefined}
        />
      </div>
      {interval_name.toLowerCase() === 'weekly' && (
        <div className={cn.row}>
          <div className={cn.weekRow}>
            {week_days.map(({ day, id }, index) => (
              <div
                onClick={() =>
                  onWeekDaySelect(
                    String(interval_bitmask),
                    index,
                    interval_bitmask !== null && +interval_bitmask[index] === 1,
                  )
                }
                key={id}
                className={classNames([cn.round], {
                  [cn.selected]: interval_bitmask !== null && +interval_bitmask[index] === 1,
                })}
              >
                <span>{day}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <DropdownCalendar
        label={`Start Date (${getTimeZone()})`}
        selected_date={start_datetime_formatted}
        onSelect={(item: { selected_date: string }) => onChange({ start_datetime_formatted: item.selected_date })}
        margin="16px 0px 0px 0px"
        calendar_time
      />
      {scheduled_batch_id && (
        <DropdownCalendar
          label={`Next Date (${getTimeZone()})`}
          selected_date={next_datetime_formatted}
          onSelect={(value: { selected_date: string }) => onChange({ next_datetime_formatted: value.selected_date })}
          margin="16px 0px 0px 0px"
          calendar_time
        />
      )}
      <div className={cn.break} style={{ borderTop: theme.border }} />
      <p className={cn.section} style={{ color: theme.font_color }}>
        Finish ({getTimeZone()}):
      </p>
      <div>
        <div className={cn.group}>
          <div className={cn.radio} onClick={() => onChange({ finish_datetime_formatted: null })}>
            <FontAwesomeIcon
              className={classNames(cn.dot, { [cn.green]: !finish_datetime_formatted })}
              icon={faCircle as IconProp}
            />
            <p style={{ color: theme.font_color }}>Never</p>
          </div>
        </div>
        <div className={cn.group}>
          <div
            className={cn.radio}
            onClick={() =>
              onChange({ finish_datetime_formatted: formatDate(new Date(), { format: 'M/D/YY hh:mm A' }) })
            }
          >
            <FontAwesomeIcon
              className={classNames(cn.dot, { [cn.green]: finish_datetime_formatted })}
              icon={faCircle as IconProp}
            />
            <p style={{ color: theme.font_color }}>On</p>
          </div>
          <div className={classNames(cn.defaultSvg, { [cn.svg]: is_nullable__date })}>
            <DropdownCalendar
              selected_date={finish_datetime_formatted}
              onSelect={onFinishDateSelect}
              calendar_display={false}
              disabled={!finish_datetime_formatted}
              calendar_time
              nullable
            />
          </div>
        </div>
      </div>
    </div>
  );
};
